<template>
  <div class="box p-4 mb-4">
    <div
      class="is-flex is-flex-direction-column is-justify-content-space-between is-align-items-center mb-3"
      style="gap: 10px"
    >
      <label class="label" style="margin-bottom: 0">
        {{ getFieldLabelName(entity, fieldKey) }}
      </label>
      <div class="buttons are-small">
        <b-button
          type="is-success"
          :outlined="resolved !== 'local'"
          :disabled="fieldKey === 'version'"
          :data-testid="`conflict-keep-mine-${fieldKey}`"
          @click="$emit('resolve-field', index, fieldKey, 'local')"
        >
          {{ $t('conflict.keep_mine') }}
        </b-button>
        <b-button
          type="is-danger"
          :outlined="resolved !== 'server'"
          :data-testid="`conflict-use-theirs-${fieldKey}`"
          @click="$emit('resolve-field', index, fieldKey, 'server')"
        >
          {{ $t('conflict.use_theirs') }}
        </b-button>
      </div>
    </div>

    <div
      class="is-flex is-align-items-center is-justify-content-space-between mb-3"
    >
      <small class="has-text-grey">
        <b-icon icon="clock" size="is-small" class="mr-1" />
        {{ $t('conflict.last_changed_by') }}
        <span class="has-text-weight-semibold">
          {{ entity.serverEntity.lastUpdatedBy || $t('conflict.unknown') }}
        </span>
        {{ timeAgo(entity.serverEntity.modified) }}
      </small>
    </div>

    <div v-if="customComponent">
      <component
        :is="customComponent"
        :local="change.local"
        :server="change.server"
        :localEntity="entity.localEntity"
        :serverEntity="entity.serverEntity"
        :resolved="resolved"
        :module="module"
      />
    </div>

    <div v-else-if="fieldsWithoutDisplay.includes(fieldKey)"></div>

    <div v-else class="columns is-vcentered">
      <div class="column">
        <label class="label">Yours</label>
        <b-input
          :value="getFieldValue(change.local)"
          readonly
          expanded
          class="is-family-monospace"
        />
      </div>
      <div class="column">
        <label class="label">Theirs</label>
        <b-input
          :value="getFieldValue(change.server)"
          readonly
          expanded
          class="is-family-monospace"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getFieldLabelName } from '../../utils/conflict-labels';
import ConflictInstructionActionField from '../conflict-fields/ConflictInstructionActionField';
import ConflictMultipleChoiceField from '../conflict-fields/ConflictMultipleChoiceField';
import ConflictMeasurementsField from '../conflict-fields/ConflictMeasurementsField';
import ConflictImagesField from '../conflict-fields/ConflictImagesField';
import ConflictInstructionTableField from '../conflict-fields/ConflictInstructionTableField';
import { FIELD_VALUES_OVERWRITE } from '../../constants/conflict-constants';

export default {
  name: 'FieldResolver',
  props: {
    index: { type: Number, required: true },
    entity: { type: Object, required: true },
    fieldKey: { type: String, required: true },
    change: { type: Object, required: true },
    resolved: { type: String, required: false },
    customComponent: { type: [String, Object], required: false },
    module: { type: String, default: null },
    fieldsWithoutDisplay: { type: Array, required: true },
  },
  components: {
    ConflictInstructionActionField,
    ConflictInstructionTableField,
    ConflictMultipleChoiceField,
    ConflictMeasurementsField,
    ConflictImagesField,
  },
  mounted() {
    this._timeAgoInterval = setInterval(() => {
      this.$forceUpdate();
    }, 60 * 1000);
  },
  beforeDestroy() {
    clearInterval(this._timeAgoInterval);
  },
  methods: {
    getFieldLabelName,
    timeAgo(isoDate) {
      if (!isoDate) return 'Unknown time';
      const diffMs = Date.now() - new Date(isoDate).getTime();

      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const months = Math.floor(days / 30);
      const years = Math.floor(months / 12);

      if (seconds < 60)
        return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
      if (minutes < 60)
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
      if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    },
    getFieldValue(fieldValue) {
      if (
        FIELD_VALUES_OVERWRITE &&
        FIELD_VALUES_OVERWRITE[fieldValue] !== undefined
      ) {
        return String(this.formatValue(FIELD_VALUES_OVERWRITE[fieldValue]));
      }
      return String(this.formatValue(fieldValue)) || '';
    },
    formatValue(val) {
      if (val === null || val === undefined) return 'N/A';
      if (typeof val === 'object') return JSON.stringify(val, null, 2);
      const normalized = String(val).toLowerCase();
      const map = this.$root.valueTranslationMap || {};
      return map[normalized] || val;
    },
  },
};
</script>

<style>
.is-flex-direction-column {
  flex-direction: column;
}
</style>
